/**
 * Demo data UX — Welcome modal on first login + sticky banner when demo loaded.
 *
 *  - Welcome modal shows the first time a user lands in the app, tracked via a
 *    per-user server flag (User.welcomeSeen). Greets each user once across any
 *    browser/device. Decoupled from demo-data presence.
 *
 *  - "Load demo project" → POST /demo-data/seed (fire-and-forget on the server;
 *    single-flight guarded). The modal STAYS OPEN showing a progress bar with
 *    step labels and polls /demo-data/status. When the data lands it reloads the
 *    app so the board renders the fresh, complete dataset. If it stalls or
 *    errors, the user gets an honest message with Keep waiting / Try again /
 *    Skip — never a silent disappear into a stuck "Loading…" page.
 *
 *  - "Skip" → dismisses the welcome. Demo can be loaded later from
 *    Admin → Import/Export.
 *
 *  - Sticky banner: shown whenever demo data is present, with a delete button.
 */

import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { demoDataApi, authApi } from '../lib/api';
import { captureEvent } from '../lib/analytics';
import { useAuthStore } from '../store/useAuthStore';
import { useFilterStore } from '../store/useFilterStore';
import { demoWelcomeDescription, demoDeleteSummary } from '../lib/demoCopy';
import type { DemoStats } from '../types';

type SeedPhase = 'idle' | 'loading' | 'done' | 'timeout' | 'error';

// Estimated-progress step labels (the server runs one batched seed and doesn't
// report granular progress, so the bar eases toward ~92% over the expected
// duration and snaps to 100% when /status confirms completion).
const SEED_STEPS: { until: number; label: string }[] = [
  { until: 25,  label: 'Creating departments & people…' },
  { until: 52,  label: 'Building sprints…' },
  { until: 80,  label: 'Generating tickets…' },
  { until: 101, label: 'Logging time & allocations…' },
];
const SEED_TIMEOUT_MS = 75_000;

export default function DemoDataBanner() {
  const { user, updateUser } = useAuthStore();
  const [present, setPresent]       = useState(false);
  const [loaded, setLoaded]         = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting]     = useState(false);
  const [seedPhase, setSeedPhase]   = useState<SeedPhase>('idle');
  const [progress, setProgress]     = useState(0);
  const [seedError, setSeedError]   = useState<string | null>(null);
  const [seedStep, setSeedStep]     = useState<string | null>(null);
  const [stats, setStats]           = useState<DemoStats | null>(null);

  const pollTimer     = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAt     = useRef(0);

  // Initial status check — also decides whether to greet a first-time visitor
  useEffect(() => {
    let cancelled = false;
    demoDataApi.status()
      .then(r => { if (!cancelled) { setPresent(!!r.data.present); setStats(r.data.stats ?? null); } })
      .catch(() => { /* not authed or transient — banner stays hidden */ })
      .finally(() => { if (!cancelled) setLoaded(true); });

    // First-login greeting is now owned by OnboardingTour (mounted at the
    // Layout level). DemoDataBanner's inline welcome modal stays in the
    // file for the demo-load progress UI (still rendered when the user
    // explicitly triggers a load) but is no longer auto-shown.

    return () => { cancelled = true; clearTimers(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stay in sync when demo data is loaded/removed from ANOTHER surface (e.g.
  // Admin → Data Sources' DemoDataCard, or the tour seed in Layout) — both fire
  // window events. Without this the banner only reflected its one-time mount
  // check, so a load elsewhere didn't show the banner until a manual refresh.
  useEffect(() => {
    const onLoaded = () => { setPresent(true); setLoaded(true); };
    const onDeleted = () => setPresent(false);
    window.addEventListener('avium:demo-data-loaded', onLoaded);
    window.addEventListener('demo-data-deleted', onDeleted);
    return () => {
      window.removeEventListener('avium:demo-data-loaded', onLoaded);
      window.removeEventListener('demo-data-deleted', onDeleted);
    };
  }, []);

  function clearTimers() {
    if (pollTimer.current)     { clearInterval(pollTimer.current);     pollTimer.current = null; }
    if (progressTimer.current) { clearInterval(progressTimer.current); progressTimer.current = null; }
  }

  function reloadApp() { window.location.reload(); }

  function markWelcomeSeen() {
    updateUser({ welcomeSeen: true });
    authApi.markWelcomeSeen().catch(() => { /* best-effort; flag re-syncs next login */ });
  }

  function dismissWelcome() {
    clearTimers();
    markWelcomeSeen();
    setShowWelcome(false);
  }

  const stepLabel = SEED_STEPS.find(s => progress < s.until)?.label ?? SEED_STEPS[SEED_STEPS.length - 1].label;

  // Start (or resume) the progress animation + completion polling.
  function beginProgress(resume = false) {
    clearTimers();
    setSeedError(null);
    setSeedPhase('loading');
    if (!resume) setProgress(0);
    // Always (re)start the deadline — including on "Keep waiting", which
    // otherwise inherited the already-blown original deadline and re-timed-out
    // on the very next poll tick.
    startedAt.current = Date.now();

    progressTimer.current = setInterval(() => {
      setProgress(p => (p < 92 ? p + Math.max(0.4, (92 - p) * 0.05) : p));
    }, 400);

    pollTimer.current = setInterval(() => {
      if (Date.now() - startedAt.current > SEED_TIMEOUT_MS) {
        clearTimers();
        setSeedPhase('timeout');
        return;
      }
      demoDataApi.status()
        .then(r => {
          if (r.data.step) setSeedStep(r.data.step);
          // The background seed surfaces its real failure here — show it instead
          // of spinning until the timeout.
          if (r.data.error) {
            clearTimers();
            setSeedError(r.data.error);
            setSeedPhase('error');
            return;
          }
          if (r.data.present) {
            clearTimers();
            setProgress(100);
            setPresent(true);
            setSeedPhase('done');
            captureEvent('demo loaded');
            // Reload so every page fetches the fresh, complete dataset.
            setTimeout(reloadApp, 1200);
          }
        })
        .catch(() => { /* transient — keep polling until timeout */ });
    }, 2500);
  }

  async function handleLoadDemo() {
    markWelcomeSeen();   // they've engaged — don't greet again even if they bail
    beginProgress();
    try {
      await demoDataApi.seed();   // 202 fast (fire-and-forget / single-flight)
    } catch {
      clearTimers();
      setSeedPhase('error');
    }
  }

  function handleRetry() {
    beginProgress();
    demoDataApi.seed().catch(() => { clearTimers(); setSeedPhase('error'); });
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await demoDataApi.delete();
      toast.success('Demo data removed.');
      setPresent(false);
      setConfirming(false);
      // Clear filters that may target now-deleted demo data (else the board looks empty).
      useFilterStore.getState().clearFilters();
      window.dispatchEvent(new CustomEvent('demo-data-deleted'));
    } catch {
      toast.error('Failed to delete demo data.');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      {/* Sticky banner — visible whenever demo data is present */}
      {loaded && present && (
        <div className="bg-brand-primary-light dark:bg-brand-primary/25 border-b border-brand-primary/20 px-6 py-2 flex items-center justify-between gap-3 text-sm flex-shrink-0">
          <div className="flex items-center gap-2 text-brand-primary-dark dark:text-brand-primary-lighter">
            <span className="text-base leading-none">💡</span>
            <span>
              <strong>You're viewing demo data</strong> — sample tickets, sprints, and fictional resources are loaded so you can explore Avium right away. Real data you import will sit alongside until deletion.
            </span>
          </div>
          <button
            onClick={() => setConfirming(true)}
            className="text-xs font-semibold text-white bg-brand-primary hover:bg-brand-primary-dark px-3 py-1 rounded-full whitespace-nowrap"
          >
            Delete demo data
          </button>
        </div>
      )}

      {/* First-time Welcome modal */}
      {showWelcome && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
            <div className="brand-header-gradient-accent text-white px-6 py-5">
              <h2 className="text-2xl font-extrabold">Welcome to Avium!</h2>
              <p className="text-teal-200 text-sm mt-1">AI delivery intelligence + Visibility, Capacity, and Risk management across every project.</p>
            </div>

            {seedPhase === 'idle' ? (
              <>
                <div className="px-6 py-5 text-sm text-ink-muted leading-relaxed space-y-3">
                  <p>Avium turns your team data into clear answers for three questions:</p>
                  <ul className="list-disc pl-5 space-y-1 text-ink-muted">
                    <li>Who's working on what</li>
                    <li>Whether you have capacity</li>
                    <li>Where your delivery risks are</li>
                  </ul>
                  <p>
                    We've prepared a sample project — <strong>Dunder Mifflin Platform</strong> — with {demoWelcomeDescription(stats)}.
                    {' '}<strong>Load it to explore the app right away</strong>, or skip and start with your own data.
                    You can always load (or delete) demo data later from Admin → Import/Export.
                  </p>
                </div>
                <div className="px-6 py-4 bg-wash border-t border-line flex flex-wrap justify-end gap-2">
                  <button
                    onClick={dismissWelcome}
                    className="px-4 py-2 text-sm font-semibold text-ink-muted hover:bg-line rounded-lg"
                  >
                    Skip — start with my data
                  </button>
                  <button
                    onClick={handleLoadDemo}
                    className="px-5 py-2 text-sm font-semibold text-white bg-brand-primary hover:bg-brand-primary-dark rounded-lg"
                  >
                    Load demo project →
                  </button>
                </div>
              </>
            ) : (
              <div className="px-6 py-7">
                {(seedPhase === 'loading' || seedPhase === 'done') && (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-ink">
                        {seedPhase === 'done' ? 'Your demo organization is ready!' : 'Setting up your demo organization…'}
                      </span>
                      <span className="text-sm font-bold text-brand-primary dark:text-brand-accent tabular-nums">{Math.round(progress)}%</span>
                    </div>
                    <div className="h-2.5 w-full rounded-full bg-wash overflow-hidden">
                      <div
                        className="h-full bg-brand-primary rounded-full transition-all duration-300 ease-out"
                        style={{ width: `${progress}%` }}
                        role="progressbar"
                        aria-valuenow={Math.round(progress)}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      />
                    </div>
                    <p className="text-xs text-ink-faint mt-3">
                      {seedPhase === 'done'
                        ? 'Loading your organization…'
                        : <>{stepLabel} <span className="text-ink-faint">· usually about 30 seconds{seedStep ? ` · server: ${seedStep}` : ''}</span></>}
                    </p>
                  </>
                )}

                {seedPhase === 'timeout' && (
                  <>
                    <div className="text-3xl mb-2">⏳</div>
                    <h3 className="text-base font-bold text-ink mb-1">Still working…</h3>
                    <p className="text-sm text-ink-muted mb-2">
                      This is taking longer than usual. The data may still be on its way — you can keep waiting,
                      start over, or skip and explore with your own data.
                    </p>
                    {seedStep && (
                      <p className="text-xs text-ink-faint mb-5">Last server step: <span className="font-mono">{seedStep}</span></p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => beginProgress(true)} className="px-4 py-2 text-sm font-semibold text-white bg-brand-primary hover:bg-brand-primary-dark rounded-lg">Keep waiting</button>
                      <button onClick={handleRetry} className="px-4 py-2 text-sm font-semibold text-ink-muted border border-line hover:bg-wash rounded-lg">Start over</button>
                      <button onClick={dismissWelcome} className="px-4 py-2 text-sm font-semibold text-ink-faint hover:bg-wash rounded-lg">Skip</button>
                    </div>
                  </>
                )}

                {seedPhase === 'error' && (
                  <>
                    <div className="text-3xl mb-2">⚠️</div>
                    <h3 className="text-base font-bold text-ink mb-1">We couldn't load the demo data</h3>
                    <p className="text-sm text-ink-muted mb-3">
                      Something went wrong while building the demo organization. You can try again, or skip and start with your own data.
                    </p>
                    {seedError && (
                      <pre className="text-left text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2 mb-5 whitespace-pre-wrap break-words">{seedError}</pre>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <button onClick={handleRetry} className="px-4 py-2 text-sm font-semibold text-white bg-brand-primary hover:bg-brand-primary-dark rounded-lg">Try again</button>
                      <button onClick={dismissWelcome} className="px-4 py-2 text-sm font-semibold text-ink-faint hover:bg-wash rounded-lg">Skip</button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirm-delete dialog */}
      {confirming && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => !deleting && setConfirming(false)}>
          <div className="bg-card rounded-xl shadow-2xl p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-ink mb-2">Delete demo data?</h2>
            <p className="text-sm text-ink-muted mb-5">
              This will permanently remove the Dunder Mifflin sample project, its sprints, tickets, allocations, and time logs, plus {demoDeleteSummary(stats)}. Real data you've added is not affected.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirming(false)}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-ink-muted hover:bg-wash rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
              >
                {deleting ? 'Deleting…' : 'Delete demo data'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
